import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from "react-router-dom";
import DataView from "./components/DataView";
import ScrapeStarter from "./components/ScrapeStarter";
import About from "./components/About";
import ThemeToggle from "./components/ThemeToggle";
import NsfwToggle from "./components/NsfwToggle";
import ProfileModal from "./components/ProfileModal";
import { fetchScrapedData, fetchScrapeStatus } from "./api";
import { initGA, trackPageView, trackCoffeeClick, testAnalytics } from "./utils/analytics";
import "./styles/enhanced-theme.css";

// SEO Content Component - Collapsible
function SEOContent() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <section className="seo-content mt-4 mb-4">
      <div className="card shadow-sm">
        <div 
          className="card-header bg-light cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
          style={{ cursor: 'pointer' }}
        >
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="h6 mb-0 text-primary">
              📖 About Sydney Roster Scheduler
            </h2>
            <span className="badge bg-primary">
              {isExpanded ? '▲ Hide' : '▼ Learn More'}
            </span>
          </div>
        </div>
        
        {isExpanded && (
          <div className="card-body">
            <p className="card-text">
              Welcome to Sydney's most comprehensive roster tracking platform. Check daily work schedules 
              for multiple Sydney venues in one convenient location.
            </p>
            
            <p className="mb-3">
              <strong>Venues:</strong> No 5 Marrickville • Ginza Cleveland St • 479 Ginza Elizabeth St • The Nightshade Gladesville
            </p>
            
            <div className="d-flex gap-2">
              <Link to="/about" className="btn btn-primary btn-sm">
                Read Full About Page →
              </Link>
            </div>
            
            <hr className="my-3" />
            
            <div className="text-muted small">
              <p className="mb-0">
                <strong>Keywords:</strong> Sydney roster, No 5 Marrickville roster, Cleveland Street schedule, 
                Elizabeth Street roster, Gladesville schedule, aus99, aus99 forum, venue rosters Sydney
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

// Enhanced Buy Me a Coffee Button Component
function BuyMeACoffeeButton() {
  const handleClick = () => {
    trackCoffeeClick();
  };

  return (
    <a
      href="https://www.buymeacoffee.com/sydneyscheduler"
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      className="coffee-button"
    >
      <span>☕</span>
      Buy me a coffee
    </a>
  );
}

// Component to track page views
function Analytics() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null;
}

// Component to dynamically update canonical tag based on route
function CanonicalTag() {
  const location = useLocation();

  useEffect(() => {
    // Remove any existing canonical tags
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }

    // Create new canonical tag with current URL
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `https://sydneyscheduler.com${location.pathname}`;
    document.head.appendChild(canonical);

    // Update meta description and title based on route
    const metaDescription = document.querySelector('meta[name="description"]');
    const pageTitle = document.querySelector('title');
    
    if (location.pathname === '/about') {
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          'Learn about Sydney Roster Scheduler - Your trusted source for No 5 Marrickville roster, Ginza Cleveland St schedule, 479 Ginza Elizabeth St roster, and The Nightshade Gladesville roster. Updated daily with accurate venue information.'
        );
      }
      if (pageTitle) {
        pageTitle.textContent = 'About Us | Sydney Roster Scheduler - No 5 Marrickville, Ginza, Nightshade';
      }
    } else if (location.pathname === '/') {
      if (metaDescription) {
        metaDescription.setAttribute('content', 
          'Live daily roster schedules for No 5 Marrickville, Ginza Cleveland St, 479 Ginza Elizabeth St, and The Nightshade Gladesville. Updated daily with current and next day rosters.'
        );
      }
      if (pageTitle) {
        pageTitle.textContent = 'Sydney Roster Scheduler | No 5 Marrickville, Cleveland, Elizabeth St, Gladesville';
      }
    }

    // Cleanup function
    return () => {
      const canonicalToRemove = document.querySelector('link[rel="canonical"]');
      if (canonicalToRemove) {
        canonicalToRemove.remove();
      }
    };
  }, [location]);

  return null;
}

function App() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastDuration, setLastDuration] = useState("");

  const [hash, setHash] = useState(window.location.hash);

  let profileVenue = "marrickville";
  let profileName = "";
  
  if (hash.startsWith('#profile-')) {
    const hashData = decodeURIComponent(hash.replace('#profile-', ''));
    if (hashData.startsWith('gladesville-')) {
      profileVenue = "gladesville";
      profileName = hashData.replace('gladesville-', '');
    } else if (hashData.startsWith('cleveland-')) {
      profileVenue = "cleveland";
      profileName = hashData.replace('cleveland-', '');
    } else if (hashData.startsWith('marrickville-')) {
      profileVenue = "marrickville";
      profileName = hashData.replace('marrickville-', '');
    } else {
      profileName = hashData; // Fallback
    }
  }

  // Initialize Google Analytics on app load
  useEffect(() => {
    initGA();
    
    // Add test function to window for easy debugging
    window.testAnalytics = testAnalytics;
    console.log('Analytics test function available: window.testAnalytics()');

    // Subscribe to hash changes to trigger modal updates immediately
    const handleHashChange = () => setHash(window.location.hash);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await fetchScrapedData();
        setData(result);

        const scrapeStatus = await fetchScrapeStatus();
        setLastDuration(scrapeStatus?.duration_human || "");
      } catch (err) {
        setError(err.message || "Unknown error");
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  return (
    <Router>
      <Analytics />
      <CanonicalTag />
      <div className="container mt-4">
        {/* Header with title, theme toggle, and coffee button */}
        <header className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
          <div>
            <Link to="/" style={{ textDecoration: 'none' }}>
              <h1 className="h3 mb-0" style={{ color: "#1976d2" }}>Sydney Roster Scheduler</h1>
            </Link>
            <small className="text-muted">Real-time roster aggregation from multiple venues</small>
          </div>
          <div className="d-flex align-items-center">
            <NsfwToggle />
            <ThemeToggle />
            <BuyMeACoffeeButton />
          </div>
        </header>

        {/* SEO Content Section - Collapsible at bottom */}
        <Routes>
          <Route path="/" element={
            <>
              <DataView data={data} loading={loading} error={error} />
              <SEOContent />
            </>
          } />
          <Route path="/about" element={<About />} />
          <Route path="/start" element={<ScrapeStarter />} />
        </Routes>

        <ProfileModal 
          show={hash.startsWith('#profile-')} 
          profileName={profileName}
          venue={profileVenue}
          onHide={() => { 
            window.history.replaceState(null, null, ' '); 
            setHash('');
          }}
        />
        
        {/* Footer */}
        <footer className="text-center mt-5 pt-4 border-top">
          <small className="text-muted">
            Last updated: {data?.no5?.timestamp ? new Date(data.no5.timestamp).toLocaleString() : 'Never'}
            {lastDuration ? ` (Last scrape took ${lastDuration})` : ""}
          </small>
        </footer>
      </div>
    </Router>
  );
}

export default App;